import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE, money } from "@/lib/api";
import { ArrowLeft, Save, Plus, Trash2, Download, FileSignature, Camera, Pill, Activity, Package } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import EvidenceUploader from "@/components/EvidenceUploader";

const EMPTY_FORM = {
    classification: "urgencia",
    motive: "",
    mechanism: "",
    antecedents: "",
    medications: "",
    allergies: "",
    physical_exam: "",
    vitals: [],
    glasgow: 15,
    pain_scale: 0,
    triage: "verde",
    procedures: [],
    medications_given: [],
    supplies_used: [],
    items_used: [], // [{item_id, sku, label, qty, unit_cost, public_price}] — descuenta inventario
    evolution: "",
    receiving_hospital: "",
    receiving_doctor: "",
    observations: "",
    patient_signature: "",
    receiver_signature: "",
    paramedic_signature: "",
    patient_signer_name: "",
    patient_signer_relation: "paciente",
    evidences: [],
};

const TABS = [
    { id: "clinico", label: "Clínico", icon: Activity },
    { id: "vitales", label: "Vitales & Tx", icon: Activity },
    { id: "meds", label: "Medicamentos", icon: Pill },
    { id: "evidencias", label: "Evidencias", icon: Camera },
    { id: "firmas", label: "Firmas", icon: FileSignature },
];

export default function FRAP() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [existing, setExisting] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("clinico");
    const [form, setForm] = useState(EMPTY_FORM);
    const [catalog, setCatalog] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQ, setPickerQ] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/services/${id}`);
            if (r?.data) {
                setService(r.data.service);
                if (r.data.frap) {
                    setExisting(r.data.frap);
                    setForm((prev) => ({ ...prev, ...r.data.frap }));
                } else {
                    // FRAP nuevo: prepoblar firmas con tripulación auto-vinculada
                    try {
                        const pref = await api.get(`/frap/prefill/${id}`);
                        if (pref?.data) {
                            const paramedicName = pref.data.paramedic?.name || "";
                            const driverName = pref.data.driver?.name || "";
                            const medicName = pref.data.medic?.name || "";
                            // Firma del paramédico: armar con paramedic + (driver) si existe
                            let parts = [];
                            if (paramedicName) parts.push(`Paramédico: ${paramedicName}${pref.data.paramedic?.license ? ` · Lic. ${pref.data.paramedic.license}` : ""}`);
                            if (driverName) parts.push(`Operador: ${driverName}`);
                            if (medicName) parts.push(`Médico/Enf.: ${medicName}`);
                            const prefillSig = parts.join("\n");
                            setForm((prev) => ({
                                ...prev,
                                paramedic_signature: prev.paramedic_signature || prefillSig,
                                _crew_prefill: pref.data,
                            }));
                        }
                    } catch { /* prefill opcional */ }
                }
            }
        } catch (e) { console.warn(e); }
        try {
            const r = await api.get("/inventory/items");
            setCatalog(r.data || []);
        } catch { /* inventario opcional */ }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Vitals helpers
    const addVital = () => set("vitals", [...form.vitals, { time: new Date().toTimeString().slice(0, 5), ta: "", fc: "", fr: "", spo2: "", temp: "", glucosa: "" }]);
    const updVital = (i, k, v) => set("vitals", form.vitals.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const rmVital = (i) => set("vitals", form.vitals.filter((_, idx) => idx !== i));

    // Medications helpers
    const addMed = () => set("medications_given", [...(form.medications_given || []), { name: "", dose: "", route: "IV", time: new Date().toTimeString().slice(0, 5) }]);
    const updMed = (i, k, v) => set("medications_given", form.medications_given.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const rmMed = (i) => set("medications_given", form.medications_given.filter((_, idx) => idx !== i));

    // Supplies helpers
    const addSupply = () => set("supplies_used", [...(form.supplies_used || []), { name: "", qty: 1 }]);
    const updSupply = (i, k, v) => set("supplies_used", form.supplies_used.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const rmSupply = (i) => set("supplies_used", form.supplies_used.filter((_, idx) => idx !== i));

    // items_used helpers (link a inventario)
    const addInventoryItem = (it) => {
        const exists = (form.items_used || []).find((x) => x.item_id === it.id);
        if (exists) {
            set("items_used", form.items_used.map((x) => x.item_id === it.id ? { ...x, qty: (Number(x.qty) || 0) + 1 } : x));
        } else {
            set("items_used", [...(form.items_used || []), {
                item_id: it.id,
                sku: it.sku,
                label: it.name,
                qty: 1,
                unit: it.unit,
                unit_cost: Number(it.unit_cost) || 0,
                public_price: Number(it.public_price) || 0,
            }]);
        }
        setPickerOpen(false);
        setPickerQ("");
    };
    const updItem = (i, k, v) => set("items_used", form.items_used.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const rmItem = (i) => set("items_used", form.items_used.filter((_, idx) => idx !== i));

    const filteredCatalog = catalog.filter((it) => {
        if (!pickerQ) return true;
        const q = pickerQ.toLowerCase();
        return (it.name || "").toLowerCase().includes(q) || (it.sku || "").toLowerCase().includes(q);
    });
    const itemsCostTotal = (form.items_used || []).reduce((s, x) => s + (Number(x.qty) || 0) * (Number(x.unit_cost) || 0), 0);
    const itemsChargeTotal = (form.items_used || []).reduce((s, x) => s + (Number(x.qty) || 0) * (Number(x.public_price) || 0), 0);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            // Quitar campos locales que no son parte del modelo FRAP
            const { _crew_prefill, ...cleaned } = form;
            const payload = { service_id: id, ...cleaned };
            await api.post("/frap", payload);
            toast.success(existing ? "FRAP actualizado" : "FRAP guardado");
            await load();
            if (!existing) navigate(`/services/${id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!service) return <div className="text-sm text-slate-500 font-mono">Cargando...</div>;

    const sigCount = [form.patient_signature, form.receiver_signature, form.paramedic_signature].filter(Boolean).length;
    const evidenceCount = (form.evidences || []).length;

    return (
        <div className="max-w-6xl space-y-4" data-testid="frap-page">
            <Link to={`/services/${id}`} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Servicio
            </Link>

            <div className="fm-card p-5 bg-slate-900 text-white flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">FORMATO DE REGISTRO DE ATENCIÓN PREHOSPITALARIA</div>
                    <h2 className="font-display font-black text-2xl tracking-tight">FRAP · {service.folio}</h2>
                    <div className="text-xs text-slate-300 mt-1">{service.patient_name} · {service.service_type_name}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 mr-2">
                        {sigCount}/3 firmas · {evidenceCount} evidencias · {form.vitals.length} vitales · {(form.medications_given || []).length} medicamentos
                    </span>
                    {existing && (
                        <a href={`${API_BASE}/pdf/frap/${id}`} target="_blank" rel="noreferrer" className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm px-4 py-2 text-sm font-bold inline-flex items-center gap-2" data-testid="download-frap-pdf">
                            <Download className="w-3.5 h-3.5" /> Descargar PDF
                        </a>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="fm-card p-1 flex items-center gap-1 overflow-x-auto" data-testid="frap-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-2 whitespace-nowrap transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            <form onSubmit={submit} className="space-y-4">
                {tab === "clinico" && (
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Cuadro clínico</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="fm-label">Clasificación</label>
                                <select value={form.classification} onChange={(e) => set("classification", e.target.value)} className="fm-input" data-testid="classification-select">
                                    <option value="traslado">Traslado</option>
                                    <option value="urgencia">Urgencia</option>
                                    <option value="atencion_sitio">Atención en sitio</option>
                                    <option value="evento">Evento / Cobertura</option>
                                    <option value="falsa_alarma">Falsa alarma</option>
                                </select>
                            </div>
                            <div>
                                <label className="fm-label">Triage</label>
                                <select value={form.triage} onChange={(e) => set("triage", e.target.value)} className="fm-input" data-testid="triage-select">
                                    <option value="verde">Verde - No urgente</option>
                                    <option value="amarillo">Amarillo - Urgente</option>
                                    <option value="rojo">Rojo - Crítico</option>
                                    <option value="negro">Negro - Fallecido</option>
                                </select>
                            </div>
                            <div className="md:col-span-2"><label className="fm-label">Motivo de atención</label><input value={form.motive ?? ""} onChange={(e) => set("motive", e.target.value)} className="fm-input" data-testid="motive-input" /></div>
                            <div><label className="fm-label">Mecanismo de lesión</label><input value={form.mechanism ?? ""} onChange={(e) => set("mechanism", e.target.value)} className="fm-input" /></div>
                            <div><label className="fm-label">Antecedentes</label><input value={form.antecedents ?? ""} onChange={(e) => set("antecedents", e.target.value)} className="fm-input" /></div>
                            <div><label className="fm-label">Medicamentos previos</label><input value={form.medications ?? ""} onChange={(e) => set("medications", e.target.value)} className="fm-input" /></div>
                            <div><label className="fm-label">Alergias</label><input value={form.allergies ?? ""} onChange={(e) => set("allergies", e.target.value)} className="fm-input" /></div>
                            <div className="md:col-span-2"><label className="fm-label">Exploración física</label><textarea value={form.physical_exam ?? ""} onChange={(e) => set("physical_exam", e.target.value)} className="fm-input" rows={2} /></div>
                            <div><label className="fm-label">Glasgow (3-15)</label><input type="number" min="3" max="15" value={form.glasgow ?? 15} onChange={(e) => set("glasgow", parseInt(e.target.value) || 0)} className="fm-input" /></div>
                            <div><label className="fm-label">Escala de dolor (0-10)</label><input type="number" min="0" max="10" value={form.pain_scale ?? 0} onChange={(e) => set("pain_scale", parseInt(e.target.value) || 0)} className="fm-input" /></div>
                        </div>
                        <div className="mt-4">
                            <label className="fm-label">Recepción</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input value={form.receiving_hospital ?? ""} onChange={(e) => set("receiving_hospital", e.target.value)} placeholder="Hospital receptor" className="fm-input" />
                                <input value={form.receiving_doctor ?? ""} onChange={(e) => set("receiving_doctor", e.target.value)} placeholder="Médico receptor" className="fm-input" />
                                <textarea value={form.evolution ?? ""} onChange={(e) => set("evolution", e.target.value)} placeholder="Evolución" className="fm-input md:col-span-2" rows={2} />
                                <textarea value={form.observations ?? ""} onChange={(e) => set("observations", e.target.value)} placeholder="Observaciones generales" className="fm-input md:col-span-2" rows={2} />
                            </div>
                        </div>
                    </div>
                )}

                {tab === "vitales" && (
                    <>
                        <div className="fm-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-display font-bold text-base">Signos vitales</h3>
                                <button type="button" onClick={addVital} className="fm-btn-outline" data-testid="add-vital-btn"><Plus className="w-3.5 h-3.5" /> Agregar toma</button>
                            </div>
                            {form.vitals.length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-400">Sin tomas registradas</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="border-b border-slate-200">{["Hora", "T/A", "FC", "FR", "SpO2", "Temp", "Glucosa", ""].map((h) => <th key={h} className="text-left py-1 px-1 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                                        <tbody>
                                            {form.vitals.map((v, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    {["time", "ta", "fc", "fr", "spo2", "temp", "glucosa"].map((k) => (
                                                        <td key={k} className="py-1 px-1"><input value={v[k] || ""} onChange={(e) => updVital(i, k, e.target.value)} className="fm-input text-xs" /></td>
                                                    ))}
                                                    <td><button type="button" onClick={() => rmVital(i)} className="text-red-500" data-testid={`rm-vital-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="fm-card p-5">
                            <h3 className="font-display font-bold text-base mb-3">Procedimientos realizados</h3>
                            <textarea value={(form.procedures || []).join("\n")} onChange={(e) => set("procedures", e.target.value.split("\n").filter(Boolean))} className="fm-input" rows={4} placeholder="Uno por línea: Oxigenoterapia 4L, Monitoreo continuo, Acceso venoso..." data-testid="procedures-input" />
                        </div>
                    </>
                )}

                {tab === "meds" && (
                    <>
                        <div className="fm-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-display font-bold text-base">Medicamentos administrados</h3>
                                <button type="button" onClick={addMed} className="fm-btn-outline" data-testid="add-med-btn"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                            </div>
                            {(form.medications_given || []).length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-400">Sin medicamentos administrados</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead><tr className="border-b border-slate-200">{["Medicamento", "Dosis", "Vía", "Hora", ""].map((h) => <th key={h} className="text-left py-1 px-1 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                                    <tbody>
                                        {form.medications_given.map((m, i) => (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-1 px-1"><input value={m.name || ""} onChange={(e) => updMed(i, "name", e.target.value)} className="fm-input text-xs" placeholder="Ej: Ácido acetilsalicílico 300mg" /></td>
                                                <td className="py-1 px-1"><input value={m.dose || ""} onChange={(e) => updMed(i, "dose", e.target.value)} className="fm-input text-xs" placeholder="300mg" /></td>
                                                <td className="py-1 px-1">
                                                    <select value={m.route || "IV"} onChange={(e) => updMed(i, "route", e.target.value)} className="fm-input text-xs">
                                                        {["IV", "IM", "SC", "VO", "SL", "Inh", "Rect", "Top"].map((r) => <option key={r} value={r}>{r}</option>)}
                                                    </select>
                                                </td>
                                                <td className="py-1 px-1"><input type="time" value={m.time || ""} onChange={(e) => updMed(i, "time", e.target.value)} className="fm-input text-xs" /></td>
                                                <td><button type="button" onClick={() => rmMed(i)} className="text-red-500" data-testid={`rm-med-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="fm-card p-5 border-l-4 border-l-emerald-500">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <div>
                                    <h3 className="font-display font-bold text-base flex items-center gap-2">
                                        <Package className="w-4 h-4 text-emerald-600" /> Insumos del inventario
                                    </h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                        Al guardar el FRAP, estos items se <strong>descuentan automáticamente</strong> del botiquín de la ambulancia asignada y se calcula el costo real / cargo al cliente.
                                    </p>
                                </div>
                                <button type="button" onClick={() => setPickerOpen((v) => !v)} className="fm-btn-primary" data-testid="open-inventory-picker">
                                    <Plus className="w-3.5 h-3.5" /> Agregar del catálogo
                                </button>
                            </div>

                            {pickerOpen && (
                                <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3 mb-3">
                                    <input
                                        autoFocus
                                        value={pickerQ}
                                        onChange={(e) => setPickerQ(e.target.value)}
                                        placeholder="Buscar por SKU, nombre, medicamento, equipo..."
                                        className="fm-input mb-2"
                                        data-testid="inventory-picker-search"
                                    />
                                    <div className="max-h-60 overflow-y-auto divide-y divide-emerald-100">
                                        {filteredCatalog.slice(0, 60).map((it) => (
                                            <button
                                                key={it.id}
                                                type="button"
                                                onClick={() => addInventoryItem(it)}
                                                className="w-full flex items-center justify-between gap-3 py-2 px-2 hover:bg-white text-left"
                                                data-testid={`pick-item-${it.sku}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{it.name}</div>
                                                    <div className="text-[10px] text-slate-500 font-mono">{it.sku} · {it.unit} · {it.type}</div>
                                                </div>
                                                <div className="text-xs font-mono tabular-nums text-emerald-700 font-bold">{money(it.public_price)}</div>
                                            </button>
                                        ))}
                                        {filteredCatalog.length === 0 && (
                                            <div className="text-center py-4 text-xs text-slate-400">Sin resultados</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {(form.items_used || []).length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-400">Sin insumos del inventario seleccionados</div>
                            ) : (
                                <>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200">
                                                {["SKU", "Insumo", "Cant.", "Costo", "Cargo", ""].map((h) => (
                                                    <th key={h} className="text-left py-1 px-1 text-[10px] font-bold text-slate-500 uppercase">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.items_used.map((x, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    <td className="py-1 px-1 font-mono text-[10px] text-slate-500">{x.sku}</td>
                                                    <td className="py-1 px-1 text-xs">{x.label}</td>
                                                    <td className="py-1 px-1 w-20">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.5"
                                                            value={x.qty || 0}
                                                            onChange={(e) => updItem(i, "qty", parseFloat(e.target.value) || 0)}
                                                            className="fm-input text-xs text-right"
                                                            data-testid={`item-qty-${i}`}
                                                        />
                                                    </td>
                                                    <td className="py-1 px-1 text-right font-mono text-[11px] tabular-nums text-slate-500">{money((Number(x.qty) || 0) * (Number(x.unit_cost) || 0))}</td>
                                                    <td className="py-1 px-1 text-right font-mono text-[11px] tabular-nums font-bold">{money((Number(x.qty) || 0) * (Number(x.public_price) || 0))}</td>
                                                    <td><button type="button" onClick={() => rmItem(i)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="border-t-2 border-slate-300 bg-emerald-50/40">
                                                <td colSpan="3" className="py-2 px-1 text-right text-[10px] uppercase tracking-widest font-bold text-slate-600">Totales</td>
                                                <td className="py-2 px-1 text-right font-mono tabular-nums text-xs">{money(itemsCostTotal)}</td>
                                                <td className="py-2 px-1 text-right font-mono tabular-nums font-bold text-emerald-700">{money(itemsChargeTotal)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                    {!service?.unit_id && (
                                        <div className="text-[11px] text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded-sm">
                                            ⚠️ Este servicio no tiene ambulancia asignada — los items no podrán descontarse de stock. Asigna una unidad antes de cerrar el FRAP.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="fm-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-display font-bold text-base">Insumos manuales (texto libre)</h3>
                                <button type="button" onClick={addSupply} className="fm-btn-outline" data-testid="add-supply-btn"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                            </div>
                            <p className="text-[11px] text-slate-500 -mt-2 mb-2">Usa esta sección sólo si necesitas registrar un insumo que <strong>no está</strong> en el catálogo. No descuenta inventario.</p>
                            {(form.supplies_used || []).length === 0 ? (
                                <div className="text-center py-6 text-sm text-slate-400">Sin insumos registrados</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead><tr className="border-b border-slate-200">{["Insumo", "Cantidad", ""].map((h) => <th key={h} className="text-left py-1 px-1 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                                    <tbody>
                                        {form.supplies_used.map((s, i) => (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-1 px-1"><input value={s.name || ""} onChange={(e) => updSupply(i, "name", e.target.value)} className="fm-input text-xs" placeholder="Ej: Catéter 18G" /></td>
                                                <td className="py-1 px-1 w-24"><input type="number" min="0" value={s.qty || 0} onChange={(e) => updSupply(i, "qty", parseFloat(e.target.value) || 0)} className="fm-input text-xs" /></td>
                                                <td><button type="button" onClick={() => rmSupply(i)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}

                {tab === "evidencias" && (
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-1">Evidencias fotográficas</h3>
                        <p className="text-xs text-slate-500 mb-4">Adjunta fotos del lugar, lesiones, INE, póliza o cualquier evidencia clínica/legal. Las imágenes se comprimen automáticamente.</p>
                        <EvidenceUploader
                            value={form.evidences || []}
                            onChange={(next) => set("evidences", next)}
                        />
                    </div>
                )}

                {tab === "firmas" && (
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-1">Firmas digitales</h3>
                        <p className="text-xs text-slate-500 mb-4">Capture la firma del paciente o responsable, del médico receptor en hospital y del paramédico responsable.</p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="space-y-2">
                                <SignaturePad
                                    label="Firma del paciente / responsable"
                                    value={form.patient_signature}
                                    onChange={(v) => set("patient_signature", v)}
                                    testId="patient-signature"
                                />
                                <input
                                    value={form.patient_signer_name || ""}
                                    onChange={(e) => set("patient_signer_name", e.target.value)}
                                    placeholder="Nombre de quien firma"
                                    className="fm-input text-xs"
                                    data-testid="patient-signer-name"
                                />
                                <select
                                    value={form.patient_signer_relation || "paciente"}
                                    onChange={(e) => set("patient_signer_relation", e.target.value)}
                                    className="fm-input text-xs"
                                    data-testid="patient-signer-relation"
                                >
                                    <option value="paciente">Paciente</option>
                                    <option value="familiar">Familiar / responsable</option>
                                    <option value="autoridad">Autoridad</option>
                                    <option value="testigo">Testigo</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <SignaturePad
                                    label="Firma médico receptor"
                                    value={form.receiver_signature}
                                    onChange={(v) => set("receiver_signature", v)}
                                    testId="receiver-signature"
                                />
                                <input
                                    value={form.receiving_doctor || ""}
                                    onChange={(e) => set("receiving_doctor", e.target.value)}
                                    placeholder="Médico receptor"
                                    className="fm-input text-xs"
                                />
                                <input
                                    value={form.receiving_hospital || ""}
                                    onChange={(e) => set("receiving_hospital", e.target.value)}
                                    placeholder="Hospital receptor"
                                    className="fm-input text-xs"
                                />
                            </div>

                            <div className="space-y-2">
                                <SignaturePad
                                    label="Firma paramédico responsable"
                                    value={form.paramedic_signature}
                                    onChange={(v) => set("paramedic_signature", v)}
                                    testId="paramedic-signature"
                                />
                                {form._crew_prefill && (
                                    <div className="text-[10px] text-emerald-700 bg-emerald-50 border-l-2 border-emerald-400 px-2 py-1 leading-relaxed" data-testid="crew-prefill-banner">
                                        🪄 <b>Tripulación auto-detectada</b>:
                                        {form._crew_prefill.paramedic?.name && <div>• Paramédico: {form._crew_prefill.paramedic.name}{form._crew_prefill.paramedic.license ? ` (Lic. ${form._crew_prefill.paramedic.license})` : ""}</div>}
                                        {form._crew_prefill.driver?.name && <div>• Operador: {form._crew_prefill.driver.name}</div>}
                                        {form._crew_prefill.medic?.name && <div>• Médico/Enf.: {form._crew_prefill.medic.name}</div>}
                                    </div>
                                )}
                                <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono pt-1">
                                    Folio servicio: {service.folio}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-slate-100 py-3 -mx-1 px-1 border-t border-slate-200">
                    <div className="text-[11px] text-slate-500 font-mono">
                        {existing ? `Última edición: ${(existing.updated_at || existing.created_at || "").slice(0, 16).replace("T", " ")}` : "Borrador sin guardar"}
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to={`/services/${id}`} className="fm-btn-outline">Cancelar</Link>
                        <button type="submit" disabled={saving} className="fm-btn-accent" data-testid="save-frap-btn">
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Guardando..." : existing ? "Actualizar FRAP" : "Guardar FRAP"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
